import { useCallback } from "react";
import _ from "lodash";

import { colors } from "theme/palette";

import Select from "components/Select";
import Stack from "components/Stack";
import Text from "components/Text";
import { useConfirmationDialog } from "components/useConfirmationDialog";

import { IDocumentType } from "entities/AISummaries/sdk";

export interface IDocumentTypeSelect {
  documentTypes: Array<IDocumentType>;
  onChange: ({ newDocumentTypeId }: { newDocumentTypeId: number }) => void;
  value?: string;
  disabled?: boolean;
  showConfirmation?: boolean;
}

const DocumentTypeSelect = ({
  documentTypes,
  onChange,
  value,
  disabled = false,
  showConfirmation = true,
}: IDocumentTypeSelect) => {
  const { show: showConfirmationDialog } = useConfirmationDialog();

  const handleDocumentTypeChange = useCallback(
    (event: React.ChangeEvent<{ value: string }>) => {
      const newDocumentTypeId = Number(event.target.value);

      if (_.isNil(value)) {
        onChange({ newDocumentTypeId });
      } else if (showConfirmation) {
        showConfirmationDialog({
          onConfirm: () => onChange({ newDocumentTypeId }),
          confirmButtonText: "Yes, modify it",
          message: (
            <>
              <Text variant="h2" marginBottom={1}>
                Modifying document type
              </Text>
              <Text variant="text2">
                Changing the document type will delete all previously generated
                answers under the current document type. Are you sure you want
                to proceed?
              </Text>
            </>
          ),
        });
      } else {
        onChange({ newDocumentTypeId });
      }
    },
    [value, showConfirmation, onChange, showConfirmationDialog]
  );

  return (
    <Stack spacing={0.5}>
      <Text variant="label" color={colors.gray60} fontWeight={600}>
        Document Type
      </Text>
      <Select
        fullWidth
        options={documentTypes.map((documentType) => ({
          label: documentType.name,
          value: documentType.id.toString(),
        }))}
        value={value}
        onChange={handleDocumentTypeChange}
        disabled={disabled}
      />
    </Stack>
  );
};

export default DocumentTypeSelect;
